import { useEffect, useRef, useState } from 'react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'

import KampanieService from '@/services/KampanieService'

export default function KampanieCombo({
	value = null,
	selectedName = '',
	onChange,
	disabled = false,
	placeholder = 'Szukaj kampanii po nazwie...',
}) {
	const [query, setQuery] = useState(selectedName || '')
	const [items, setItems] = useState([])
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const containerRef = useRef(null)

	useEffect(() => {
		setQuery(selectedName || '')
	}, [selectedName])

	useEffect(() => {
		if (!open) return

		let cancelled = false
		const loadItems = async () => {
			setLoading(true)
			setError(null)
			const searchQuery = (query || '').trim()
			const options = { limit: 200, sortColumn: '_id', sortDirection: 'desc' }
			const result = searchQuery
				? await KampanieService.search(searchQuery, options)
				: await KampanieService.getFiltered(options)

			if (cancelled) return

			if (result?.error) {
				setError(result.error)
				setItems([])
			} else {
				setItems(Array.isArray(result?.data) ? result.data : [])
			}
			setLoading(false)
		}

		const timer = setTimeout(loadItems, 200)
		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [open, query])

	useEffect(() => {
		const handleClickOutside = event => {
			if (!containerRef.current?.contains(event.target)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleSelect = kampania => {
		setOpen(false)
		setQuery(kampania?.nazwa || '')
		onChange?.(kampania)
	}

	const handleInputChange = event => {
		const next = event.target.value
		setQuery(next)
		setOpen(true)
		if (value) {
			onChange?.(null)
		}
	}

	const handleKeyDown = event => {
		if (event.key === 'ArrowDown') {
			setOpen(true)
		}
		if (event.key === 'Enter' && open && items.length > 0) {
			event.preventDefault()
			handleSelect(items[0])
		}
	}

	return (
		<div className="relative" ref={containerRef}>
			<div
				className={`flex items-center rounded-md border ${
					disabled ? 'bg-gray-100 text-gray-500' : 'bg-white'
				} border-gray-300 focus-within:ring-2 focus-within:ring-indigo-500`}>
				<input
					type="text"
					disabled={disabled}
					value={query}
					onChange={handleInputChange}
					onFocus={() => setOpen(true)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className="block w-full rounded-l-md border-0 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
				/>
				<button
					type="button"
					disabled={disabled}
					onClick={() => setOpen(prev => !prev)}
					className="flex h-full items-center rounded-r-md border-l border-gray-200 px-2 text-gray-500 hover:text-gray-700 focus:outline-none">
					<ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
				</button>
			</div>

			{open && !disabled && (
				<div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
					<div className="max-h-56 overflow-y-auto py-1">
						{loading ? (
							<div className="px-3 py-2 text-sm text-gray-500">Ladowanie kampanii...</div>
						) : error ? (
							<div className="px-3 py-2 text-sm text-red-600">{error}</div>
						) : items.length > 0 ? (
							items.map(kampania => {
								const id = kampania?._id || kampania?.id
								const isSelected = id === value
								return (
									<button
										key={id || kampania?.nazwa}
										type="button"
										onMouseDown={event => event.preventDefault()}
										onClick={() => handleSelect(kampania)}
										className={`flex w-full items-center px-3 py-2 text-left text-sm ${
											isSelected
												? 'bg-indigo-50 font-medium text-indigo-700'
												: 'text-gray-700 hover:bg-gray-50'
										}`}>
										<span className="truncate">{kampania?.nazwa || '-'}</span>
									</button>
								)
							})
						) : (
							<div className="px-3 py-2 text-sm text-gray-500">Nie znaleziono kampanii</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
